'use client';

import s from './styles.module.scss';
import Image from 'next/image';
import { FormEvent, useState } from 'react';
import { createLead } from '../../lib/payments';
import { Modal } from '../../components/ui/Modal';
import { ContactSchema } from '@/src/components/forms/contactForm.schema';

type Errors = Partial<{
    name: string;
    age: string;
    countryCode: string;
    phone: string;
    preferCall: string;
    preferTelegram: string;
    contactMethod: string;
    form: string;
}>;

export const ContactForm = () => {
    const [name, setName] = useState('');
    const [age, setAge] = useState('');
    const [countryCode, setCountryCode] = useState('+380');
    const [phone, setPhone] = useState('');
    const [preferCall, setPreferCall] = useState(true);
    const [preferTelegram, setPreferTelegram] = useState(false);

    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errors, setErrors] = useState<Errors>({});
    const [openModal, setOpenModal] = useState(false);

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});

        try {
            await ContactSchema.validate(
                { name, age, countryCode, phone, preferCall, preferTelegram },
                { abortEarly: false }
            );
        } catch (err: any) {
            if (err?.inner?.length) {
                const next: Errors = {};
                err.inner.forEach((e: any) => {
                    if (e.path && !next[e.path as keyof Errors]) next[e.path as keyof Errors] = e.message;
                });
                setErrors(next);
            } else if (err?.message) {
                setErrors({ form: err.message });
            }
            return;
        }

        const fullPhone = `${countryCode}${phone}`.trim();
        const fullName = `${name} ${preferCall ? 'тф' : ''} ${preferTelegram ? 'тг' : ''} ${
            age ? `вік:${age}` : ''
        }`.trim();

        const payload = {
            source_id: 5,
            pipeline_id: 2,
            contact: {
                full_name: fullName,
                phone: fullPhone,
            },
        };

        try {
            setStatus('loading');
            const data = await createLead(payload);
            if (!data || data.result === false) {
                throw new Error('Помилка при відправці');
            }

            setStatus('success');

            setName('');
            setAge('');
            setPhone('');
            setPreferCall(true);
            setPreferTelegram(false);

            setOpenModal(true);
        } catch (err) {
            setStatus('error');
            setErrors((prev) => ({ ...prev, form: 'Сталася помилка при відправці' }));
        } finally {
            if (!openModal) {
                window.setTimeout(() => setStatus('idle'), 3000);
            }
        }
    };

    return (
        <section className={s.root}>
            <div>
                <div>
                    <h2>
                        Забронюй свій неймовірний<br/>
                        тиждень осені!
                    </h2>
                    <Image src="/icons/line_price.svg" alt="" width={354} height={65} />
                </div>

                <form onSubmit={onSubmit} noValidate>
                    <div className={s.inputContainer}>
                        <div>
                            <div
                                className={`${s.inputPhone} ${
                                    errors.phone || errors.countryCode ? s.inputPhoneError : ''
                                }`}
                            >
                                <select
                                    aria-label="Код країни"
                                    value={countryCode}
                                    onChange={(e) => setCountryCode(e.target.value)}
                                    aria-invalid={!!errors.countryCode}
                                >
                                    <option value="+380">+380</option>
                                    <option value="+48">+48</option>
                                    <option value="+1">+1</option>
                                </select>
                                <span aria-hidden className={s.line} />
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    aria-label="Номер телефону"
                                    placeholder="00 000 00 00"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                                    required
                                    aria-invalid={!!errors.phone}
                                />
                            </div>
                            {(errors.phone || errors.countryCode) && (
                                <span className={s.error}>{errors.phone || errors.countryCode}</span>
                            )}
                        </div>

                        <div>
                            <input
                                aria-label="Ім'я"
                                placeholder="Ім'я"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                aria-invalid={!!errors.name}
                                className={errors.name ? s.inputError : undefined}
                            />
                            {errors.name && <span className={s.error}>{errors.name}</span>}
                        </div>

                        <div>
                            <input
                                aria-label="Вік"
                                placeholder="Вік"
                                value={age}
                                onChange={(e) => setAge(e.target.value.replace(/[^\d]/g, ''))}
                                className={`${s.age} ${errors.age ? s.inputError : ''}`}
                                aria-invalid={!!errors.age}
                            />
                            {errors.age && <span className={s.error}>{errors.age}</span>}
                        </div>
                    </div>

                    <div className={s.submitContainer}>
                        <label className={s.checkbox}>
                            <input
                                type="checkbox"
                                checked={preferCall}
                                onChange={(e) => {
                                    setPreferCall(e.target.checked);
                                    if (e.target.checked) setPreferTelegram(false);
                                }}
                                aria-invalid={!!errors.contactMethod}
                            />
                            <span className={s.checkmark} />
                            <p className={s.stackText}>
                                <span>дзвінок</span>
                                <span>по телефону</span>
                            </p>
                        </label>

                        <label className={s.checkbox}>
                            <input
                                type="checkbox"
                                checked={preferTelegram}
                                onChange={(e) => {
                                    setPreferTelegram(e.target.checked);
                                    if (e.target.checked) setPreferCall(false);
                                }}
                                aria-invalid={!!errors.contactMethod}
                            />
                            <span className={s.checkmark} />
                            <p className={s.stackText}>
                                <span>повідомлення</span>
                                <span>у Telegram</span>
                            </p>
                        </label>

                        <button type="submit" disabled={status === 'loading'}>
                            {status === 'loading' ? 'Надсилаємо…' : 'Забронювати місце'}
                        </button>
                    </div>

                    {errors.contactMethod && <p className={s.error}>{errors.contactMethod}</p>}
                    {errors.form && <p className={s.errorMsg}>{errors.form}</p>}
                    {status === 'success' && <p className={s.successMsg}>Дані успішно надіслані!</p>}
                </form>
            </div>

            <Modal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                }}
            >
                <div
                    style={{
                        padding: '26px 50px',
                        textAlign: 'center',
                        background: '#0124f1',
                        color: '#ffffff',
                        borderRadius: '20px',
                        height: '115px',
                    }}
                >
                    <p>Ми вже отримали твої дані, і звʼяжемося протягом 1 дня</p>
                </div>
            </Modal>
        </section>
    );
};

export default ContactForm;
